<template>
  <BaseWidget
    class="data-table"
    :title="content.title"
    :widgetId="content.id"
    :edit="edit"
    :persistent="true"
    :content="content"
    :isWidgetLoading="isWidgetLoading"
    :getWidgetReport="getWidgetReport"
  >
    <template v-slot:widget-control>
      <div class="widget-controls">
        <ButtonSecondary v-if="edit" :disabled="!canRecalculate" @click="handleRecalculateClick">
          {{ preparedRecalculateTitle }}
        </ButtonSecondary>

        <ButtonSecondary
          v-if="edit"
          :disabled="!canReset"
          style="margin-left: 8px; width: 120px"
          @click="handleResetClick"
        >
          {{ Lang.reset }}
        </ButtonSecondary>

        <v-tooltip bottom v-if="!isTimeDistribution">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              height="48"
              @click="showColumnDialog = !showColumnDialog"
              class="elevation-0 show-columns-btn action-icon"
              v-bind="attrs"
              v-on="on"
            >
              <template v-slot:default>
                <span class="custom-loader">
                  <v-icon light>mdi-format-columns</v-icon>
                </span>
              </template>
            </v-btn>
          </template>
          <span>{{ Lang.showColumns }}</span>
        </v-tooltip>
      </div>
    </template>
    <template v-slot:default="{ onCanEditConfigChange }">
      <ProjectOutputTable
        v-if="content?.settings?.distribution === METRIC_DISTRIBUTION"
        ref="projectTable"
        :data="data"
        :edit="edit"
        :headers="headers"
        :headerLabels.sync="headerLabels"
        :isWidgetLoading="isWidgetLoading"
        :content="content"
        :summary-row="summaryRow"
        :footer-props="footerProps"
        @onColumnsChange="onColumnsChange"
        @onUpdate="onUpdate"
        @canRecalculateChange="canRecalculate = $event"
        @canResetChange="canReset = $event"
        @clickPlanLocation="onClickPlanLocation"
        class="overflow-auto"
      />
      <ProjectOutputTimeTable
        v-if="content?.settings?.distribution === TIME_DISTRIBUTION"
        ref="projectTimeTable"
        :data="data"
        :edit="edit"
        :headers="headers"
        :headerLabels.sync="headerLabels"
        :isWidgetLoading="isWidgetLoading"
        :content="content"
        :summary-row="summaryRow"
        :footer-props="footerProps"
        @canRecalculateChange="canRecalculate = $event"
        @canResetChange="canReset = $event"
        @canEditConfigChange="
          (val) => {
            onCanEditConfigChange(val);
            canEditConfig = val;
          }
        "
        @onColumnsChange="onColumnsChange"
        @clickPlanLocation="onClickPlanLocation"
        @onBudgetInTimeChange="onBudgetInTimeChange"
        @onUpdate="onUpdate"
      />
      <ProjectTableColumnsDrawer
        v-model="showColumnDialog"
        @onColumnsChange="onColumnsChange"
        :selected-header="headerLabels"
      />
      <LocationsDrawer v-model="isLocationsOpen" :selection="locationSelection" />
    </template>
  </BaseWidget>
</template>

<script lang="ts">
type LocationSelection = {
  states: string[];
  cities: string[];
  dma: string[];
  zip_codes: string[];
};

import { cloneDeep } from "lodash";
import Vue from "vue";
import { DataOptions } from "vuetify";
import { ThemeOptions } from "vuetify/types/services/theme";
import { mapGetters, mapMutations } from "vuex";

import ProjectWidgetApi from "@/classes/ProjectWidgetApi";
import ButtonSecondary from "@/components/customComponents/ButtonSecondary.vue";
import LocationsDrawer from "@/components/drawers/LocationsDrawer.vue";
import ProjectTableColumnsDrawer from "@/components/drawers/ProjectTableColumnsDrawer.vue";
import ProjectOutputTable from "@/components/tables/ProjectOutputTable.vue";
import ProjectOutputTimeTable from "@/components/tables/ProjectOutputTimeTable.vue";
import BaseWidget from "@/components/widgets/plan/BaseWidget.vue";
import { METRIC_DISTRIBUTION, TIME_DISTRIBUTION } from "@/constants";
import { formatters } from "@/helpers/formatters";
import { collectTimeKeysFromTree, makeTimeHeaderTitle } from "@/helpers/planWidgets";
import Lang from "@/lang";
import inputsDictionary from "@/settings/inputsDictionary";
import planTableMetrics from "@/settings/metrics/planTableMetrics";
import { defaultPlanTableHeaders, defaultPlanTableTimeHeaders } from "@/settings/planTableSettings";
import { PlanInputDictionary } from "@/types/common";
import { ContentWidget } from "@/types/layouts";
import { Scenario as FullScenario, Project } from "@/types/project";

export default Vue.extend({
  name: "TableWidget",

  props: {
    content: Object as () => ContentWidget,
    edit: Boolean,
  },

  components: {
    LocationsDrawer,
    ButtonSecondary,
    ProjectOutputTimeTable,
    ProjectOutputTable,
    ProjectTableColumnsDrawer,
    BaseWidget,
  },

  watch: {
    filterParams: {
      deep: true,
      async handler(): Promise<void> {
        this.isWidgetLoading = true;
        await this.onUpdate();
        this.isWidgetLoading = false;
      },
    },
    projectFiltered: {
      deep: true,
      immediate: true,
      async handler(): Promise<void> {
        this.isWidgetLoading = true;
        await this.onUpdate();
        this.isWidgetLoading = false;
      },
    },
    timeMode: {
      immediate: false,
      async handler(newVal: string, oldVal: string) {
        if (newVal === oldVal) return;
        await this.refreshForTimeModeChange();
      },
    },
    canEditConfig: {
      immediate: true,
      handler(val: boolean) {
        this.$emit("canEditConfigChange", val);
      },
    },

    "content.settings.distribution": {
      immediate: false,
      async handler(newVal: string, oldVal: string) {
        if (newVal === oldVal) return;
        this.isWidgetLoading = true;
        await this.onUpdate();
        await this.setHeaders();
        this.isWidgetLoading = false;
      },
    },
  },

  async beforeMount() {
    this.theme = this.$vuetify.theme;
    await this.onUpdate();
    await this.setHeaders();
  },

  data() {
    return {
      isLocationsOpen: false as boolean,
      locationSelection: {
        states: [],
        cities: [],
        dma: [],
        zip_codes: [],
      } as LocationSelection,
      METRIC_DISTRIBUTION,
      TIME_DISTRIBUTION,
      Lang,
      options: {} as DataOptions,
      isWidgetLoading: false,
      data: [] as Record<string, unknown>[],
      headerLabels: [...defaultPlanTableHeaders],
      showColumnDialog: false,
      theme: {} as ThemeOptions,
      canRecalculate: false,
      canReset: false,
      canEditConfig: false,
      displayMetrics: inputsDictionary.displayMetrics,
      summaryRow: {} as Record<string, unknown>,
      footerProps: {
        showCurrentPage: true,
        disableItemsPerPage: true,
        disablePagination: false,
      },
      isRefreshing: false as boolean,
    };
  },
  computed: {
    ...mapGetters("userSettings", ["filterParams"]),
    ...mapGetters("projectOutput", ["projectFiltered", "isLoading", "filters"]),

    preparedRecalculateTitle(): string {
      return this.content?.settings?.distribution === METRIC_DISTRIBUTION ? Lang.recalculate : Lang.applyChanges;
    },
    headers(): PlanInputDictionary[] {
      return this.headerLabels;
    },

    widgetId(): string {
      return this.content?.id.replace(" ", "");
    },

    selectedMetrics(): string[] {
      return this.headers.map((item: PlanInputDictionary) => {
        return String(item.value);
      });
    },

    isTimeDistribution(): boolean {
      return this.content?.settings?.["distribution"] === TIME_DISTRIBUTION;
    },

    isMetricDistribution(): boolean {
      return this.content?.settings?.distribution === METRIC_DISTRIBUTION;
    },
    timeMode(): string {
      const mode = (this.content?.settings?.granularity as string) || "month";
      return ["week", "month", "quarter"].includes(mode) ? mode : "month";
    },
  },

  methods: {
    ...mapMutations("app", ["showAlert"]),

    async refreshForTimeModeChange(): Promise<void> {
      if (this.isRefreshing) return;
      this.isRefreshing = true;
      this.isWidgetLoading = true;

      try {
        this.canRecalculate = false;
        this.canReset = false;

        const t = this.$refs.projectTimeTable as Vue & { resetEdits?: () => void };
        t?.resetEdits?.();

        await this.onUpdate();
        await this.setHeaders();
      } finally {
        this.isWidgetLoading = false;
        this.isRefreshing = false;
      }
    },
    onBudgetInTimeChange(
      patches: Array<{
        scenario_id: string;
        kpi: string;
        channel: string;
        distribution: Array<{ start_date: string; end_date: string; daily_budget: number }>;
      }>
    ) {
      this.$store.dispatch("projectOutput/updateBudgetInTime", patches);
      this.showAlert({ message: "Local Project updated. Save layout to persist changes.", type: "info" });
    },
    isSettingsColumnsDefault(settingsColumns?: Array<{ title: string; value: string }>): boolean {
      if (!settingsColumns || settingsColumns.length === 0) return true;
      const defaultValues = defaultPlanTableHeaders.map((h) => String(h.value));
      const currentValues = settingsColumns.map((c) => String(c.value));
      if (defaultValues.length !== currentValues.length) return false;
      for (let i = 0; i < defaultValues.length; i++) {
        if (defaultValues[i] !== currentValues[i]) return false;
      }
      return true;
    },
    handleRecalculateClick(): void {
      if (this.isMetricDistribution) {
        (this.$refs.projectTable as Vue & { onClickPreparePayload?: () => void })?.onClickPreparePayload?.();
      } else if (this.isTimeDistribution) {
        (this.$refs.projectTimeTable as Vue & { onClickPreparePayload?: () => void })?.onClickPreparePayload?.();
      }
    },
    handleResetClick(): void {
      if (this.isMetricDistribution) {
        (this.$refs.projectTable as Vue & { onClickResetEdits?: () => void })?.onClickResetEdits?.();
      } else if (this.isTimeDistribution) {
        (this.$refs.projectTimeTable as Vue & { resetEdits?: () => void })?.resetEdits?.();
      }
    },
    findScenarioById(root: Project, wantedId: string): FullScenario | undefined {
      const stack: Record<string, unknown>[] = Array.isArray(root)
        ? ([...root] as Record<string, unknown>[])
        : [root as unknown as Record<string, unknown>];

      while (stack.length) {
        const node = stack.pop();
        if (!node || typeof node !== "object") continue;

        const nodeId = String(node["id"] ?? node["scenario_id"] ?? "");
        if (nodeId && nodeId === wantedId) return node as unknown as FullScenario;

        const childKeys = ["scenarios", "children", "plans", "plan_children", "variants", "rows"];
        for (const k of childKeys) {
          const v = node[k];
          if (Array.isArray(v)) stack.push(...(v as Record<string, unknown>[]));
        }

        for (const v of Object.values(node)) {
          if (Array.isArray(v)) stack.push(...(v as Record<string, unknown>[]));
          else if (v && typeof v === "object") stack.push(v as Record<string, unknown>);
        }
      }
      return undefined;
    },
    async setHeaders(): Promise<void> {
      if (this.content?.settings?.distribution === TIME_DISTRIBUTION && Array.isArray(this.data) && this.data.length) {
        const mode = this.timeMode;
        const timeKeys: string[] = collectTimeKeysFromTree(this.data as unknown as never);

        const defaultHeaders: PlanInputDictionary[] = cloneDeep(defaultPlanTableTimeHeaders).map((h) => ({
          ...h,
          editable: false,
        }));

        const timeHeaders: PlanInputDictionary[] = timeKeys.map((k) => {
          if (mode === "month") {
            return {
              title: k,
              value: k,
              lvl: 2,
              tableGroup: "standard",
              formatter: formatters.formattedCurrencyNoFraction,
              headerFormatter: formatters.monthOnly,
              editable: true,
            };
          } else {
            return {
              title: makeTimeHeaderTitle(k, mode),
              value: k,
              lvl: 2,
              tableGroup: "standard",
              formatter: formatters.formattedCurrencyNoFraction,
              editable: true,
            };
          }
        });

        this.headerLabels = [...defaultHeaders, ...timeHeaders];
      } else if (this.content?.settings?.distribution === METRIC_DISTRIBUTION) {
        const settingsColumns = (this.content?.settings?.columns ?? []) as Array<{
          title: string;
          value: string;
        }>;
        const selectedFromSettings: PlanInputDictionary[] = settingsColumns
          .map((col) => planTableMetrics.find((tm) => tm.value === col.value))
          .filter((col): col is PlanInputDictionary => !!col);
        const isUsingDefault = this.isSettingsColumnsDefault(settingsColumns);

        if (!isUsingDefault) {
          this.headerLabels = selectedFromSettings.length ? selectedFromSettings : [...defaultPlanTableHeaders];
          return;
        }

        const kpiSet = this.collectKpisFromData();
        const kpiDrivenColumns: PlanInputDictionary[] = planTableMetrics.filter(
          (m) => !!m?.kpiTrigger && kpiSet.has(String(m.kpiTrigger))
        );

        const existingValues = new Set(selectedFromSettings.map((c) => String(c.value)));
        const additions = kpiDrivenColumns.filter((c) => !existingValues.has(String(c.value)));
        const mergedByValue = new Map<string, PlanInputDictionary>();

        [...selectedFromSettings, ...additions].forEach((c) => {
          const key = String(c.value);
          if (!mergedByValue.has(key)) mergedByValue.set(key, c);
        });

        const nextHeaders = Array.from(mergedByValue.values());
        const prevValues = this.headerLabels.map((h) => String(h.value));
        const nextValues = nextHeaders.map((h) => String(h.value));
        const changed = prevValues.length !== nextValues.length || prevValues.some((v, i) => v !== nextValues[i]);
        this.headerLabels = nextHeaders;

        if (changed) {
          this.$emit("onColumnsChange", { columns: this.headerLabels, id: this.content.id, isDefault: false });
        }
      } else {
        this.headerLabels = [...defaultPlanTableHeaders];
        this.$emit("onColumnsChange", {
          columns: this.headerLabels,
          id: this.content.id,
          isDefault: true,
        });
      }
    },

    collectKpisFromData(): Set<string> {
      const out = new Set<string>();
      const pushKpi = (node: Record<string, unknown>) => {
        const k = node?.["campaign_kpi"];
        if (k) out.add(String(k));
      };
      const walk = (node: unknown): void => {
        if (!node) return;
        if (Array.isArray(node)) return node.forEach(walk);
        if (typeof node !== "object") return;
        const n = node as Record<string, unknown>;
        pushKpi(n);
        if (Array.isArray(n["rows"])) (n["rows"] as unknown[]).forEach(walk);
        ["children", "plans", "plan_children", "variants"].forEach((k) => {
          if (Array.isArray(n[k])) walk(n[k]);
        });
      };
      walk(this.data);
      return out;
    },

    async getData(): Promise<void> {
      if (!this.projectFiltered) return;
      this.isWidgetLoading = true;

      const projectWidgetApi = new ProjectWidgetApi();
      if (this.content?.settings?.["distribution"] === TIME_DISTRIBUTION) {
        if (this.timeMode === "quarter") {
          this.data = projectWidgetApi.flattenCampaignDataTreeByQuarter(this.projectFiltered) as Record<
            string,
            unknown
          >[];
        } else if (this.timeMode === "week") {
          this.data = projectWidgetApi.flattenCampaignDataTreeByWeek(this.projectFiltered) as Record<string, unknown>[];
        } else {
          this.data = projectWidgetApi.flattenCampaignDataTreeByMonth(this.projectFiltered) as Record<
            string,
            unknown
          >[];
        }
      } else {
        this.data = projectWidgetApi.flattenCampaignDataTree(this.projectFiltered) as Record<string, unknown>[];
      }
      this.isWidgetLoading = false;
    },
    mapHeadersToSettingsColumns(cols: PlanInputDictionary[]) {
      return cols.map((c) => ({ title: c.title, value: String(c.value) }));
    },

    onColumnsChange(val: PlanInputDictionary[]): void {
      this.headerLabels = val;
      this.$emit("onColumnsChange", { columns: val, id: this.content.id });

      const updatedContent: ContentWidget = {
        ...this.content,
        settings: {
          ...(this.content?.settings ?? {}),
          distribution: this.content?.settings?.distribution ?? "metric",
          columns: this.mapHeadersToSettingsColumns(val),
        },
      };
      this.$emit("inputSummary", updatedContent);

      this.onUpdate();
    },

    onClickPlanLocation(scenarioId: string): void {
      const scenario = this.findScenarioById(this.projectFiltered, scenarioId);
      if (!scenario) {
        console.warn(`[TableWidget] Scenario not found by id=${scenarioId}`);
        return;
      }

      const sel: LocationSelection = {
        states: Array.isArray(scenario?.states) ? (scenario.states as string[]) : [],
        cities: Array.isArray(scenario?.cities) ? (scenario.cities as string[]) : [],
        dma: Array.isArray(scenario?.dma) ? (scenario.dma as string[]) : [],
        zip_codes: Array.isArray(scenario?.zip_codes) ? (scenario.zip_codes as string[]) : [],
      };

      this.locationSelection = sel;
      this.isLocationsOpen = true;
    },

    async onUpdate(): Promise<void> {
      await this.getData();
    },

    async getWidgetReport(): Promise<void> {
      await this.$nextTick();

      if (this.isMetricDistribution) {
        const child = this.$refs.projectTable as Vue & { exportProcessedData?: () => void };
        if (child?.exportProcessedData) return child.exportProcessedData();
      } else if (this.isTimeDistribution) {
        const child = this.$refs.projectTimeTable as Vue & { exportProcessedData?: () => void };
        if (child?.exportProcessedData) return child.exportProcessedData();
      }

      console.warn("[TableWidget] export method not available for current distribution");
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/scss/theme";
@import "@/scss/custom_variables.scss";

.data-table {
  .widget-controls {
    display: flex;
    align-items: center;
  }
  .select {
    margin-right: 10px !important;
    border-radius: 4px;
  }
}
</style>

<style lang="scss">
@import "@/scss/theme";

.v-select__selection--comma {
  font-size: 16px !important;
  font-weight: 500;
}
.show-columns-btn {
  border: 1px solid rgba(255, 255, 255, 0.24) !important;
  font-size: 16px !important;
  margin-left: 12px;
}
.show-columns-btn:hover {
  border: 1px solid rgba(255, 255, 255, 1) !important;
}
.v-application.theme--dark,
.pdf-container.theme--dark {
  .data-table {
    .select.v-input__control {
      border-radius: 4px !important;
    }
    .v-select__selection--comma {
      color: var(--v-content-white-base);
    }
    .v-text-field--filled > .v-input__control > .v-input__slot,
    .v-text-field--full-width > .v-input__control > .v-input__slot,
    .v-text-field--outlined > .v-input__control > .v-input__slot {
      background-color: var(--v-background-medium-base);
    }
    .show-columns-btn {
      background-color: var(--v-medium-base);
    }
  }
}
.v-application.theme--light,
.pdf-container.theme--light {
  .data-table {
    .v-text-field--filled > .v-input__control > .v-input__slot,
    .v-text-field--full-width > .v-input__control > .v-input__slot,
    .v-text-field--outlined > .v-input__control > .v-input__slot {
      background-color: var(--v-content-light-base);
    }
    .show-columns-btn {
      background-color: var(--v-background-medium-base);
    }
  }
}
</style>
